import React, { useEffect, useState } from 'react';
import { formatPhoneInput } from '@/lib/utils';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, MapPin, Video, Mail, Phone, CheckCircle2 } from 'lucide-react';
import { publicAPI } from '@/lib/publicApi';
import AddToCalendar from '@/components/calendar/AddToCalendar';
import EventCover from '@/components/calendar/EventCover';
import { formatEventDate, formatEventTime, tzAbbrev } from '@/lib/eventTime';

export default function PublicEventDetailPage() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    publicAPI.getPublicEvent(id)
      .then((r) => setEvent(r.data))
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="py-24 text-center text-sm text-slate-500" data-testid="event-detail-loading">Loading event…</div>;
  }
  if (notFound || !event) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="event-detail-not-found">
        <h1 className="text-2xl font-bold text-slate-900">Event not available</h1>
        <p className="mt-3 text-slate-600">This event is not currently published for public viewing.</p>
        <Link to="/events" className="mt-6 inline-flex items-center gap-2 text-[#0f2a5e] font-semibold hover:underline">
          <ArrowLeft size={16} /> Back to events
        </Link>
      </div>
    );
  }

  const loc = event.physical_location || {};
  const hasLoc = ['street','city','state','zip'].some((k) => (loc[k] || '').trim());
  const isVirtual = !!event.virtual_meeting_url;

  return (
    <div className="bg-slate-50 min-h-[80vh]" data-testid="public-event-detail">
      {/* Phase 26.9 — Hero banner. If a cover image is set, use it as
          the wide 16:9 hero; otherwise fall back to the branded default
          placeholder (rendered inside EventCover). Title chip stays
          overlaid so the page never looks empty. */}
      <div className="bg-[#0a1f47] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4">
          <Link to="/events" className="inline-flex items-center gap-2 text-slate-200 hover:text-white text-sm">
            <ArrowLeft size={16} /> Back to events
          </Link>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <div className="relative rounded-xl overflow-hidden shadow-xl">
            <EventCover
              coverUrl={event.cover_image_url
                ? `${process.env.REACT_APP_BACKEND_URL}${event.cover_image_url}`
                : null}
              title={event.title}
              category={event.category}
              variant="hero"
              showTitleOnPlaceholder={false}
            />
            {/* Subtle gradient scrim so the title chip is legible even
                against bright photography. */}
            <div
              aria-hidden
              className="absolute inset-x-0 bottom-0 h-32 pointer-events-none"
              style={{ background: 'linear-gradient(to top, rgba(10,31,71,0.85), transparent)' }}
            />
            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
              <span className="inline-block px-2.5 py-1 rounded bg-white/15 backdrop-blur text-[10px] font-semibold uppercase tracking-wider text-amber-200">
                {event.category?.replace(/_/g,' ') || 'Event'}
              </span>
              <h1
                className="mt-2 text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-white"
                data-testid="event-detail-title"
              >
                {event.title}
              </h1>
              <div className="mt-3">
                <a
                  href="#register"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-400 text-[#0f2a5e] text-sm font-bold shadow-lg hover:bg-amber-300 transition"
                  data-testid="hero-register-cta"
                >
                  Register — it&apos;s free
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <FlyerImage eventId={event.event_id} />
          {event.description && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-[#b8862d] mb-3">About this event</h2>
              <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{event.description}</p>
            </div>
          )}
          <PublicRegistrationBlock event={event} />
        </div>

        <aside className="space-y-4">
          <a
            href="#register"
            className="lg:sticky lg:top-4 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#0f2a5e] text-white text-sm font-semibold shadow-md hover:bg-[#173d82] transition"
            data-testid="sidebar-register-cta"
          >
            Register for this event →
          </a>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-3" data-testid="event-detail-info">
            <InfoRow icon={<Calendar size={16} />} label="Date"
              value={event.start_datetime
                ? new Date(event.start_datetime).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: event.timezone || undefined })
                : '—'} />
            {event.start_datetime && !event.all_day && (
              <InfoRow icon={<Clock size={16} />} label="Time"
                value={`${formatEventTime(event.start_datetime, event.timezone)}${event.end_datetime ? ' – ' + formatEventTime(event.end_datetime, event.timezone) : ''}`} />
            )}
            {event.all_day && <InfoRow icon={<Clock size={16} />} label="Time" value="All day" />}

            {hasLoc && (
              <InfoRow icon={<MapPin size={16} />} label="Location"
                value={[loc.street, loc.city, loc.state, loc.zip].filter(Boolean).join(', ') || 'TBD'} />
            )}
            {isVirtual && (
              <InfoRow icon={<Video size={16} />} label="Virtual event"
                value={event.virtual_meeting_url ? (
                  <a href={event.virtual_meeting_url} target="_blank" rel="noreferrer"
                    className="text-[#0f2a5e] hover:underline break-all" data-testid="event-detail-meeting-link">
                    {event.virtual_meeting_url}
                  </a>
                ) : 'Details will be shared with attendees'} />
            )}

            {(event.contact_name || event.contact_email || event.contact_phone) && (
              <div className="pt-3 border-t border-slate-100 min-w-0">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Contact</div>
                {event.contact_name && <div className="text-sm text-slate-700 break-words">{event.contact_name}</div>}
                {event.contact_email && (
                  <a
                    href={`mailto:${event.contact_email}`}
                    className="flex items-start gap-1.5 text-sm text-[#0f2a5e] hover:underline break-all"
                    data-testid="public-event-contact-email"
                  >
                    <Mail size={12} className="mt-1 flex-shrink-0" />
                    <span className="min-w-0 break-all">{event.contact_email}</span>
                  </a>
                )}
                {event.contact_phone && (
                  <a
                    href={`tel:${event.contact_phone}`}
                    className="flex items-center gap-1.5 text-sm text-[#0f2a5e] hover:underline break-all"
                    data-testid="public-event-contact-phone"
                  >
                    <Phone size={12} className="flex-shrink-0" />
                    <span className="min-w-0 break-all">{event.contact_phone}</span>
                  </a>
                )}
              </div>
            )}

            {/* Phase 26.7 — Add to Calendar (public event) */}
            <div className="pt-3 border-t border-slate-100">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                <Calendar size={12} className="inline mr-1" /> Add to calendar
              </div>
              <AddToCalendar variant="public" eventId={event.event_id} />
            </div>
          </div>

          <div className="bg-[#0f2a5e] text-white rounded-xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold">Interested in joining KR?</h3>
            <p className="text-xs text-slate-200 mt-1">
              Submit a brief interest form and our team will follow up.
            </p>
            <Link to="/become-an-agent"
              className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#b8862d] text-white text-sm font-semibold hover:bg-[#a67520] transition">
              Get Started
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="w-6 h-6 rounded bg-slate-100 text-slate-600 flex items-center justify-center mt-0.5">{icon}</div>
    <div className="min-w-0">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm text-slate-800">{value}</div>
    </div>
  </div>
);

const FlyerImage = ({ eventId }) => {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <img
      src={publicAPI.publicFlyerUrl(eventId)}
      alt=""
      onError={() => setFailed(true)}
      className="w-full rounded-xl border border-slate-200 shadow-sm"
      data-testid="event-detail-flyer"
    />
  );
};

// Phase 26.2A — Anonymous public registration form + confirmation.
function PublicRegistrationBlock({ event }) {
  const [confirmed, setConfirmed] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    agency: '', company: '', npn: '', registration_notes: '', guest_count: 0,
  });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState(null);

  const cap = event.capacity;
  const registered = event.registered_count ?? 0;
  const remaining = event.remaining_seats ?? (cap == null ? null : Math.max(0, cap - registered));
  const isFull = cap != null && remaining === 0;

  const deadline = event.registration_deadline ? new Date(event.registration_deadline) : null;
  const deadlinePassed = deadline && deadline < new Date();
  const allowLate = !!event.allow_late_registration;
  const registrationBlocked = isFull || (deadlinePassed && !allowLate);
  const allowGuests = !!event.registration_allow_guests;
  const maxGuests = event.max_guests_per_registration || 0;

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const requiresNpn = !!event.requires_npn;

  const validate = () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = 'Required';
    if (!form.last_name.trim()) errs.last_name = 'Required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = 'Valid email required';
    if (requiresNpn) {
      const npnDigits = (form.npn || '').replace(/\D/g, '');
      if (!npnDigits) errs.npn = 'NPN is required for this event';
      if (!form.phone.trim()) errs.phone = 'Phone is required when providing an NPN';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        agency: form.agency.trim() || null,
        company: form.company.trim() || null,
        npn: form.npn.trim() || null,
        registration_notes: form.registration_notes.trim() || null,
        guest_count: Number(form.guest_count) || 0,
      };
      const { data } = await publicAPI.registerForPublicEvent(event.event_id, payload);
      setConfirmed(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Registration failed. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <div className="bg-white rounded-xl border border-emerald-200 p-6 shadow-sm space-y-4" data-testid="public-registration-confirmation">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle2 className="text-emerald-600" size={22} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">You&apos;re registered</h2>
            <p className="text-xs text-slate-500">{confirmed.message || 'We have your registration on file.'}</p>
          </div>
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm border-t border-slate-100 pt-4">
          <ConfKV label="Event" value={event.title} />
          <ConfKV label="Confirmation" value={confirmed.confirmation_id?.slice(0, 8) || '—'} />
          <ConfKV label="Date"
            value={event.start_datetime ? formatEventDate(event.start_datetime, event.timezone) : '—'} />
          <ConfKV label="Time"
            value={event.start_datetime ? formatEventTime(event.start_datetime, event.timezone) : '—'} />
          <ConfKV label="Location" value={[event.physical_location?.city, event.physical_location?.state].filter(Boolean).join(', ') || (event.virtual_meeting_url ? 'Virtual' : 'TBD')} />
          <ConfKV label="Status" value="Registered" />
          {confirmed.guest_count > 0 && (
            <ConfKV label="Guests" value={confirmed.guest_count} />
          )}
        </dl>
        <p className="text-[11px] text-slate-500 border-t border-slate-100 pt-3">
          Please save this confirmation. A reminder email will be sent closer to the event date.
        </p>
        <div className="pt-2">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
            Add to your calendar
          </div>
          <AddToCalendar variant="public" eventId={event.event_id} />
        </div>
      </div>
    );
  }

  return (
    <div id="register" className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-4 scroll-mt-24" data-testid="public-registration-form">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Save your spot</h2>
          <p className="text-xs text-slate-500 mt-0.5">Registration is free and takes less than a minute.</p>
        </div>
        {cap != null && (
          <span className="text-xs text-slate-500" data-testid="public-registration-capacity">
            {registered} / {cap} registered · {remaining} left
          </span>
        )}
      </div>

      {registrationBlocked ? (
        <div className="rounded-md bg-rose-50 border border-rose-200 p-4 text-sm text-rose-800" data-testid="public-registration-blocked">
          {isFull ? 'This event is full.' : 'Registration is closed. The deadline has passed.'}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {requiresNpn && (
            <div
              className="rounded-md bg-sky-50 border border-sky-200 p-3 text-xs text-slate-700"
              data-testid="pubreg-npn-notice"
            >
              <span className="font-semibold text-slate-900">Are you a licensed insurance agent?</span>{' '}
              Enter your NPN so we can tailor the training content and CE credits for you.
              Don&apos;t have an NPN handy? No problem — you can still register.
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextInput label="First name *" value={form.first_name} onChange={(v) => setField('first_name', v)} error={errors.first_name} testId="pubreg-first-name" />
            <TextInput label="Last name *" value={form.last_name} onChange={(v) => setField('last_name', v)} error={errors.last_name} testId="pubreg-last-name" />
            <TextInput label="Email *" type="email" value={form.email} onChange={(v) => setField('email', v)} error={errors.email} testId="pubreg-email" />
            <TextInput label={requiresNpn ? 'Phone *' : 'Phone'} value={formatPhoneInput(form.phone)} onChange={(v) => setField('phone', v)} error={errors.phone} testId="pubreg-phone" />
            <TextInput label="Agency" value={form.agency} onChange={(v) => setField('agency', v)} testId="pubreg-agency" />
            <TextInput label="Company" value={form.company} onChange={(v) => setField('company', v)} testId="pubreg-company" />
            <TextInput label={requiresNpn ? 'NPN *' : 'NPN'} value={form.npn} onChange={(v) => setField('npn', v)} error={errors.npn} testId="pubreg-npn" />
            {allowGuests && (
              <div>
                <label className="text-xs font-medium text-slate-600">Guests (max {maxGuests})</label>
                <input
                  type="number" min={0} max={maxGuests}
                  value={form.guest_count}
                  onChange={(e) => setField('guest_count', Math.max(0, Math.min(maxGuests, Number(e.target.value) || 0)))}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  data-testid="pubreg-guest-count"
                />
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Notes (optional)</label>
            <textarea
              rows={2}
              value={form.registration_notes}
              onChange={(e) => setField('registration_notes', e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              placeholder="Anything the organizer should know?"
              data-testid="pubreg-notes"
            />
          </div>

          {error && (
            <div className="rounded-md bg-rose-50 border border-rose-200 p-3 text-sm text-rose-800" data-testid="pubreg-error">
              {typeof error === 'string' ? error : 'Registration failed. Please try again.'}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0f2a5e] text-white text-sm font-semibold hover:bg-[#173d82] transition disabled:opacity-50"
            data-testid="pubreg-submit"
          >
            {submitting ? 'Registering…' : 'Register'}
          </button>

          <p className="text-[11px] text-slate-500 leading-relaxed" data-testid="pubreg-privacy">
            By registering, you agree to be contacted by KR Enterprise Group with details
            about this training and other similar opportunities. Your information is used
            only to coordinate the event and follow-up communications. Registration is free
            and creates no obligation — you can opt out at any time.
          </p>
        </form>
      )}
    </div>
  );
}

const TextInput = ({ label, value, onChange, type = 'text', error, testId }) => (
  <div>
    <label className="text-xs font-medium text-slate-600">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${error ? 'border-rose-400' : 'border-slate-300'}`}
      data-testid={testId}
    />
    {error && <p className="mt-1 text-[11px] text-rose-600">{error}</p>}
  </div>
);

const ConfKV = ({ label, value }) => (
  <>
    <dt className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</dt>
    <dd className="text-sm text-slate-800">{value}</dd>
  </>
);
